import { useEffect, useState } from "react";
import parse from "html-react-parser";
import useUpsellStore from "@/state/upsellStore";
import defaultProductImage from "@/assets/popup.webp";

export default function ModalUpsell() {
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const {
    isModalOpen,
    isSubmitting,
    closeModal,
    postUpsellData,
    getFormattedTotalPrice,
    getFormattedPrimaryOldPrice,
    getFormattedFreeProductsValue,
    getFormattedSavings,
    getFormattedShippingThreshold,
    getPrimaryProduct,
  } = useUpsellStore();

  const primaryProduct = getPrimaryProduct();
  const productImage = primaryProduct?.product_image || defaultProductImage;
  const formattedPrice = getFormattedTotalPrice();
  const formattedOldPrice = getFormattedPrimaryOldPrice();
  const freeProductsValue = getFormattedFreeProductsValue();
  const formattedSavings = getFormattedSavings();
  const formattedThreshold = getFormattedShippingThreshold();

  // Get text labels from ACF
  const config = window.upsellConfig || {};
  const heading = config.tr_heading || "We really want you to succeed on your weight loss!";
  const totalPriceLabel = config.tr_total_price_label || "One-time Payment";
  const saveLabel = config.tr_save_label || "SAVE";
  const ctaText = config.tr_cta_text || "Get the Offer";
  const processingText = config.tr_processing_text || "Processing...";
  const declineText = config.tr_decline_text || "I'll pass on the offer today";

  // Process WYSIWYG fields with placeholders
  const shippingMessage = (config.tr_shipping_message || "<strong>###threshold###</strong> away from your <strong>order shipping today</strong>")
    .replace("###threshold###", formattedThreshold);

  const regularPriceLabel = (config.tr_regular_price_label || 'Regular price: <span style="color: #ED4337; font-weight: 600;">###formattedOldPrice###</span>')
    .replace("###formattedOldPrice###", formattedOldPrice);

  const freeProductsLabel = (config.tr_free_products_label || "So we are giving you <strong>###freeProductsValue###</strong> worth Harmonic bottle for free along with two bottles of Symbiotic at a discounted price!")
    .replace("###freeProductsValue###", freeProductsValue);

  const handleClose = () => {
    closeModal();
  };

  const handleAccept = async () => {
    await postUpsellData();
  };

  const handleDecline = (e) => {
    if (e) e.preventDefault();
    handleClose();
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
      const timer = setTimeout(() => setShouldAnimate(true), 300);

      const handleEscape = (e) => {
        if (e.key === "Escape") handleClose();
      };
      document.addEventListener("keydown", handleEscape);

      return () => {
        document.body.style.overflow = "";
        clearTimeout(timer);
        document.removeEventListener("keydown", handleEscape);
      };
    } else {
      document.body.style.overflow = "";
      setShouldAnimate(false);
    }
  }, [isModalOpen, handleClose]);

  return (
    <>
      {isModalOpen && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/70 backdrop-blur-[8px] z-[1000] flex items-center justify-center p-4">
          <div className="bg-white rounded-[32px] w-full max-w-[520px] max-h-screen overflow-y-auto relative animate-modal-fade-in [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:bg-transparent">
            <div className="p-0 flex flex-col">
              {/* Modal Header */}
              <div className="relative p-0">
                <img
                  src={productImage}
                  alt="Product"
                  className="w-full h-auto rounded-none block"
                />
                <button
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 border-none rounded-full flex items-center justify-center cursor-pointer text-main transition-all duration-200 backdrop-blur-[10px] hover:bg-white hover:scale-105 md:w-10 md:h-10 md:top-4 md:right-4 max-md:w-9 max-md:h-9 max-md:top-[0.8rem] max-md:right-[0.8rem]"
                  onClick={handleClose}
                >
                  <svg
                    className="w-5 h-5 text-main flex-shrink-0 align-[initial] mt-[1px] max-md:w-[18px] max-md:h-[18px]"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-0">
                <div className="bg-[#FCFBF9] text-main px-[1.3rem] pt-[0.8rem] pb-[0.9rem] text-center max-md:px-4 max-md:py-[0.6rem]">
                  <div className="text-base font-[450] mb-[0.3rem] text-rgba(90,58,83,0.85)] max-md:text-[0.8rem] max-md:mb-[0.2rem]">
                    {parse(shippingMessage)}
                  </div>
                  <div className="flex items-center gap-[0.8rem] max-md:gap-[0.6rem]">
                    <span className="text-[0.9rem] mb-[1px] font-[550] text-[#2D0C2D] flex-shrink-0 max-md:text-[0.8rem]">
                      $0
                    </span>
                    <div className="relative flex-1 h-[6px] overflow-hidden bg-[#2d0c2d09] rounded-[20px]">
                      <div
                        className={`h-full bg-[#FF6621] rounded-[20px] transition-[width] duration-1000 ease-[var(--ease-progress)] ${shouldAnimate ? "w-[85%]" : "w-0"}`}
                      ></div>
                    </div>
                    <span className="text-[0.9rem] mb-[1px] font-[550] text-[#2D0C2D] flex-shrink-0 max-md:text-[0.8rem]">
                      {formattedThreshold}
                    </span>
                  </div>
                </div>

                {formattedSavings && (
                  <div className="bg-[#FDD379] flex items-center justify-center gap-2 px-[0.7rem] py-2 rounded-xl my-[0.6rem] mx-auto mt-2 w-fit rotate-[-0.8deg] max-md:hidden max-md:px-2 max-md:py-[0.4rem] max-md:rounded-lg max-md:gap-[0.4rem] max-md:mt-[0.6rem] max-md:mb-0">
                    <svg
                      className="w-4 h-4 flex-shrink-0 mt-[1px] max-md:w-3 max-md:h-3"
                      viewBox="0 0 25 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M23.792 11.496L12.992 0.696C12.56 0.264 11.96 0 11.3 0H2.9C1.58 0 0.5 1.08 0.5 2.4V10.8C0.5 11.46 0.764 12.06 1.208 12.504L12.008 23.304C12.44 23.736 13.04 24 13.7 24C14.36 24 14.96 23.736 15.392 23.292L23.792 14.892C24.236 14.46 24.5 13.86 24.5 13.2C24.5 12.54 24.224 11.928 23.792 11.496ZM4.7 6C3.704 6 2.9 5.196 2.9 4.2C2.9 3.204 3.704 2.4 4.7 2.4C5.696 2.4 6.5 3.204 6.5 4.2C6.5 5.196 5.696 6 4.7 6Z"
                        fill="var(--color-main)"
                      />
                    </svg>
                    <span className="text-main text-[0.9rem] font-[550] max-md:text-[0.7rem]">
                      {saveLabel} {formattedSavings}
                    </span>
                  </div>
                )}
                <h2 className="font-family-title text-[1.9rem] font-normal leading-[1.25] text-[#442D15] mx-8 my-2 text-center max-md:text-[1.5em] max-md:leading-[1.3] max-md:mx-4 max-md:my-2">
                  {heading}
                </h2>
                <p className="text-[1.05rem] leading-[1.55] text-[#442d157a] mx-8 mt-[0.8rem] mb-[1.4rem] text-center max-md:text-[0.95rem] max-md:leading-[1.4] max-md:mx-4 max-md:mt-[0.6rem] max-md:mb-[1.1rem] [&_strong]:text-[#482C10]">
                  {parse(freeProductsLabel)}
                </p>
                <div className="bg-[#F9F8F4] rounded-[22px] px-8 pt-[1.2rem] pb-0 mx-4 mb-4 block text-center max-md:px-4 max-md:pt-4 max-md:pb-0 max-md:rounded-2xl max-md:mx-4 max-md:mb-4">
                  <div className="mb-4 max-md:mb-2">
                    <div className="text-[0.9rem] text-gray font-medium mb-2">
                      {totalPriceLabel}
                    </div>
                    <div className="font-family-title text-5xl font-normal text-main leading-none mb-2 max-md:text-[2.5rem]">
                      {formattedPrice}
                    </div>
                    {formattedOldPrice && (
                      <div className="text-[0.95rem] text-gray mb-4 max-md:text-[0.8rem] max-md:mb-[0.6rem]">
                        {parse(regularPriceLabel)}
                      </div>
                    )}
                  </div>
                  <button
                    className="w-full min-h-[56px] px-8 py-4 border-none rounded-[88px] bg-[#FF5700] text-white text-[1.1rem] font-family-fallback font-semibold cursor-pointer transition-all duration-200 hover:-translate-y-px active:translate-y-0 max-md:px-4 max-md:py-4 max-md:min-h-[52px] max-md:text-base disabled:opacity-70 disabled:cursor-not-allowed"
                    onClick={handleAccept}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? processingText : ctaText}
                  </button>
                  <a
                    href="#"
                    className="pt-[0.9rem] px-4 pb-[1.1rem] border-none text-main text-[1.05rem] font-normal cursor-pointer no-underline transition-all duration-200 ease-in-out inline-block hover:text-main max-md:text-[0.95rem] max-md:py-[0.9rem] max-md:px-4"
                    onClick={handleDecline}
                  >
                    {declineText}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
